/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { Injectable } from '@angular/core';
import { CAPACITY_UNIT, UNPLACEHOLDER, UtilService } from '@dm/core';
import { runningStatusE } from 'app/common/constants';
import { LicenseKey } from 'app/common/constants/license-key';
import { KeyValuePipe } from 'app/common/pipes/key-value.pipe';
import { ToolService } from 'app/common/services/tool.service';
import { type NetSpace, type Quota } from 'app/common/types/mo';
import { ProductConfig, ShardSwitch, SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { NetSpaceStrService } from 'app/feature/service/network/net-space/net-space.str.service';
import { ColumnDefsService } from 'app/feature/system/hardware/device/table/column-defs.service';
import { HardwareStrService } from 'app/feature/system/hardware/hardware-str.service';
import { pick } from 'lodash';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { type DataConfig, type Tip, DataTypeE, DataURL, GlobalSearchStrService } from '../../global-search-str.service';
import { GlobalSearchConfigService } from '../global-search-config.service';

@Injectable({ providedIn: 'root' })
export class ConfigServicesStrService {
    servicesDataMap: { [key: string]: DataConfig };

    constructor(
        private configService: GlobalSearchConfigService,
        private str: GlobalSearchStrService,
        private keyValuePipe: KeyValuePipe,
        private utilService: UtilService,
        private toolService: ToolService,
        private hardStrService: HardwareStrService,
        private lifColumnDefsService: ColumnDefsService,
        private netSpaceStrService: NetSpaceStrService,
    ) { }

    /**
     * 返回dataMap
     */
    getDataMap(): { [key: string]: DataConfig } {
        this.servicesDataMap = {
            /**
             *  租户
             */
            [DataTypeE.tenant]: {
                type: DataTypeE.tenant,
                url: '/service/v-store-service/v-store',
                operation: [
                    {
                        searchKey: 'service.vStore_task_create_title',
                        action: 'create',
                        permission: ['vstore_W'],
                        hide: ProductConfig.isHideCreateAndDeleteVStore,
                    },
                ],
                page: {
                    permission: ['@vstore_R'],
                    searchKey: 'service.vstore_term_vstores_title',
                },
                isNotSupportFeatureInOceanMicro: !FeatureConfig.IsSupportVStoreService,
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.vstore).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.runStatusLab,
                                    value: this.keyValuePipe.transform(data.RUNNINGSTATUS, runningStatusE),
                                },
                                {
                                    label: this.str.i18n.fsCountLab,
                                    value: data.fileSystemCount,
                                },
                            ];

                            return result;
                        }),
                    ),
            },

            /**
             *  LUN组
             */
            [FeatureConfig.IsReplaceStrLunWithNamespace ? DataTypeE.NamespaceGroup : DataTypeE.lunGroup]: {
                type: FeatureConfig.IsReplaceStrLunWithNamespace ? DataTypeE.NamespaceGroup : DataTypeE.lunGroup,
                url: `/service/block/${FeatureConfig.IsReplaceStrLunWithNamespace ? 'namespace-group' : 'lun-group'}`,
                tab: this.str.i18n.lunGroupTab,
                license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin],
                operation: [
                    {
                        searchKey: 'storage.LunGroup_task_create_title',
                        action: 'searchCreate',
                        permission: ['lun_group_R', 'lun_group_W'],
                        defaultSearch: true,
                    },
                ],
                page: {
                    searchKey: 'common.lunGroup_title',
                    permission: ['lun_group_R'],
                    defaultAdvancedSearch: true,
                    advancedType: this.str.i18n.serviceTitle,
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.lungroup).pipe(
                        map(({ data }) => {
                            const result: Tip[] = SWITCH.IS_DIRECT_MODE ?
                                [
                                    {
                                        label: this.str.i18n.mapStatusLab,
                                        value: data.ISADD2MAPPINGVIEW === 'true' ? this.str.i18n.mapped : this.str.i18n.unMapped,
                                    },
                                    {
                                        label: this.str.i18n.protectLab,
                                        icons: [
                                            ...(parseInt(data.snapshotGroupNum, 10) ? ['app-icon-snapshot'] : []),
                                            ...(parseInt(data.cloneGroupNum, 10) ? ['app-icon-clone'] : []),
                                            ...(parseInt(data.hyperMetroGroupNum, 10) ? ['app-icon-hypermetro'] : []),
                                            ...(parseInt(data.cdpGroupNum, 10) ? ['app-icon-hyperCDP'] : []),
                                            ...(parseInt(data.replicationGroupNum, 10) ? ['app-icon-replication'] : []),
                                            ...(parseInt(data.drStarNum, 10) ? ['app-icon-3dc'] : []),
                                        ],
                                    },
                                ] :
                                [
                                    {
                                        label: this.str.i18n.mapStatusLab,
                                        value: data.ISADD2MAPPINGVIEW === 'true' ? this.str.i18n.mapped : this.str.i18n.unMapped,
                                    },
                                    {
                                        label: this.str.i18n.usageLab,
                                        value: this.utilService.adaptUnit(data.allocatedCapacity, 'Sector').label,
                                    },
                                    {
                                        label: this.str.i18n.protectLab,
                                        icons: [
                                            ...(parseInt(data.snapshotGroupNum, 10) ? ['app-icon-snapshot'] : []),
                                            ...(parseInt(data.cloneGroupNum, 10) ? ['app-icon-clone'] : []),
                                            ...(parseInt(data.hyperMetroGroupNum, 10) ? ['app-icon-hypermetro'] : []),
                                            ...(parseInt(data.cdpGroupNum, 10) ? ['app-icon-hyperCDP'] : []),
                                            ...(parseInt(data.replicationGroupNum, 10) ? ['app-icon-replication'] : []),
                                            ...(parseInt(data.drStarNum, 10) ? ['app-icon-3dc'] : []),
                                        ],
                                    },
                                ];

                            return result;
                        }),
                    ),
            },
            /**
             *  LUN
             */
            [FeatureConfig.IsReplaceStrLunWithNamespace ? DataTypeE.Namespace : DataTypeE.lun]: {
                type: FeatureConfig.IsReplaceStrLunWithNamespace ? DataTypeE.Namespace : DataTypeE.lun,
                url: `/service/block/${FeatureConfig.IsReplaceStrLunWithNamespace ? 'namespace-group' : 'lun-group'}`,
                tab: this.str.i18n.lunTab,
                license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin],
                operation: [
                    {
                        searchKey: 'storage.lun_task_create_title',
                        action: 'searchCreate',
                        permission: ['lun_R', 'lun_W'],
                        defaultSearch: true,
                    },
                ],
                page: {
                    searchKey: 'common.LUNs',
                    permission: ['lun_R'],
                    defaultSearch: true,
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.lun).pipe(
                        map(({ data }) => {
                            // 微存储没有“HASRSSOBJECT”
                            if (FeatureConfig.IsHideProtection) {
                                const microResult: Tip[] = [
                                    {
                                        label: this.str.i18n.healthStatusLab,
                                        status: parseInt(data.HEALTHSTATUS, 10),
                                        value: this.str.statusMap[parseInt(data.HEALTHSTATUS, 10)].label,
                                    },
                                    {
                                        label: this.str.i18n.mapStatusLab,
                                        value: data.mapped === 'true' ? this.str.i18n.mapped : this.str.i18n.unMapped,
                                    },
                                ];

                                // 非直通才显示“已分配容量”
                                if (!SWITCH.IS_DIRECT_MODE) {
                                    microResult.push({
                                        label: this.str.i18n.usageLab,
                                        value: this.utilService.adaptUnit(data.ALLOCCAPACITY, 'Sector').label,
                                    });
                                }

                                return microResult;
                            }

                            const protection = pick(JSON.parse(data.HASRSSOBJECT), [
                                'SnapShot',
                                'LunClone',
                                'HyperCDP',
                                'RemoteReplication',
                                'HyperMetro',
                                'drStar',
                            ]);
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.healthStatusLab,
                                    status: parseInt(data.HEALTHSTATUS, 10),
                                    value: this.str.statusMap[parseInt(data.HEALTHSTATUS, 10)].label,
                                },
                                {
                                    label: this.str.i18n.mapStatusLab,
                                    value: data.mapped === 'true' ? this.str.i18n.mapped : this.str.i18n.unMapped,
                                },
                                {
                                    label: this.str.i18n.usageLab,
                                    value: this.utilService.adaptUnit(data.ALLOCCAPACITY, 'Sector').label,
                                },
                                {
                                    label: this.str.i18n.protectLab,
                                    icons: [
                                        ...(protection.SnapShot === 'TRUE' ? ['app-icon-snapshot'] : []),
                                        ...(protection.LunClone === 'TRUE' ? ['app-icon-clone'] : []),
                                        ...(protection.HyperCDP === 'TRUE' ? ['app-icon-hyperCDP'] : []),
                                        ...(protection.RemoteReplication === 'TRUE' ? ['app-icon-replication'] : []),
                                        ...(protection.HyperMetro === 'TRUE' ? ['app-icon-hypermetro'] : []),
                                        ...(protection.drStar === 'TRUE' ? ['app-icon-3dc'] : []),
                                    ],
                                },
                            ];

                            return result;
                        }),
                    ),
            },
            /**
             *  回收站
             */
            ...SWITCH.IS_DIRECT_MODE ? {} : {
                [DataTypeE.recycleBin]: {
                    type: DataTypeE.recycleBin,
                    url: `/service/block/${FeatureConfig.IsReplaceStrLunWithNamespace ? 'namespace-group' : 'lun-group'}`,
                    tab: this.str.i18n.recycleBinTab,
                    license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin],
                    page: {
                        searchKey: 'storage.recycleBin_task_title',
                        permission: ['recycle_bin_R'],
                    },
                    isNotSupportFeatureInOceanMicro: SWITCH.IS_DIRECT_MODE,
                    getCombinationObservable: (id: string): Observable<Tip[]> =>
                        // 使用id关联查询LUN，该接口为批量查询接口，返回有且只有一条数据的数组
                        this.configService.getData(id, DataURL.recyclebin, { ASSOCIATEOBJTYPE: 57880, ASSOCIATEOBJID: id }).pipe(
                            map(({ data }) => {
                                if (FeatureConfig.IsHideProtection) {
                                    const microResult: Tip[] = [
                                        {
                                            label: this.str.i18n.healthStatusLab,
                                            value: this.str.statusMap[parseInt(data[0].HEALTHSTATUS, 10)].label,
                                        },
                                        {
                                            label: this.str.i18n.mapStatusLab,
                                            value: data.mapped === 'true' ? this.str.i18n.mapped : this.str.i18n.unMapped,
                                        },
                                    ];

                                    // 非直通才显示“已分配容量”
                                    if (!SWITCH.IS_DIRECT_MODE) {
                                        microResult.push({
                                            label: this.str.i18n.usageLab,
                                            value: this.utilService.adaptUnit(data[0].ALLOCCAPACITY, 'Sector').label,
                                        });
                                    }

                                    return microResult;
                                }

                                const protection = pick(JSON.parse(data[0].HASRSSOBJECT), [
                                    'SnapShot',
                                    'LunClone',
                                    'HyperCDP',
                                    'RemoteReplication',
                                    'HyperMetro',
                                    'drStar',
                                ]);
                                const result: Tip[] = [
                                    {
                                        label: this.str.i18n.healthStatusLab,
                                        value: this.str.statusMap[parseInt(data[0].HEALTHSTATUS, 10)].label,
                                    },
                                    {
                                        label: this.str.i18n.mapStatusLab,
                                        value: data.mapped === 'true' ? this.str.i18n.mapped : this.str.i18n.unMapped,
                                    },
                                    {
                                        label: this.str.i18n.usageLab,
                                        value: this.utilService.adaptUnit(data[0].ALLOCCAPACITY, 'Sector').label,
                                    },
                                    {
                                        label: this.str.i18n.protectLab,
                                        icons: [
                                            ...(protection.SnapShot === 'TRUE' ? ['app-icon-snapshot'] : []),
                                            ...(protection.LunClone === 'TRUE' ? ['app-icon-clone'] : []),
                                            ...(protection.HyperCDP === 'TRUE' ? ['app-icon-hyperCDP'] : []),
                                            ...(protection.RemoteReplication === 'TRUE' ? ['app-icon-replication'] : []),
                                            ...(protection.HyperMetro === 'TRUE' ? ['app-icon-hypermetro'] : []),
                                            ...(protection.drStar === 'TRUE' ? ['app-icon-3dc'] : []),
                                        ],
                                    },
                                ];

                                return result;
                            }),
                        ),
                },
            },

            /**
             *  主机组
             */
            [DataTypeE.hostGroup]: {
                type: DataTypeE.hostGroup,
                url: '/service/block/hosts',
                tab: this.str.i18n.hostGroupTab,
                license: [LicenseKey.SmartThin],
                operation: [
                    {
                        searchKey: 'host.hostGroup_task_create_hostGroup_button',
                        action: 'searchCreate',
                        permission: ['host_group_R', 'host_group_W'],
                    },
                ],
                page: {
                    searchKey: 'common.hostGroups_title',
                    permission: ['host_group_R'],
                    defaultAdvancedSearch: true,
                    advancedType: this.str.i18n.serviceTitle,
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.hostgroup).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.hostCountLab,
                                    value: data.hostNumbe,
                                },
                            ];

                            // 微存储直通模式，不显示“已分配容量”
                            if (!SWITCH.IS_DIRECT_MODE) {
                                result.push({
                                    label: this.str.i18n.usageLab,
                                    value: this.utilService.adaptUnit(data.allocatedCapacity, 'Sector').label,
                                });
                            }

                            return result;
                        }),
                    ),
            },
            /**
             *  主机
             */
            [DataTypeE.host]: {
                type: DataTypeE.host,
                url: '/service/block/hosts',
                tab: this.str.i18n.hostTab,
                license: [LicenseKey.SmartThin],
                operation: [
                    {
                        searchKey: 'host.host_task_create_host_button',
                        action: 'searchCreate',
                        permission: ['host_R', 'host_W'],
                        defaultSearch: true,
                    },
                ],
                page: {
                    searchKey: 'common.hosts_title',
                    permission: ['host_R'],
                    defaultSearch: true,
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.host).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.healthStatusLab,
                                    status: parseInt(data.HEALTHSTATUS, 10),
                                    value: this.str.statusMap[parseInt(data.HEALTHSTATUS, 10)].label,
                                },
                                {
                                    label: this.str.i18n.ipLab,
                                    value: data.IP,
                                },
                            ];

                            // 微存储直通模式，不显示“已分配容量”
                            if (!SWITCH.IS_DIRECT_MODE) {
                                result.push({
                                    label: this.str.i18n.usageLab,
                                    value: this.utilService.adaptUnit(data.allocatedCapacity, 'Sector').label,
                                });
                            }

                            return result;
                        }),
                    ),
            },
            /**
             *  启动器
             */
            [DataTypeE.initiator]: {
                type: DataTypeE.initiator,
                url: '/service/block/hosts',
                tab: this.str.i18n.initiatorTab,
                license: [LicenseKey.SmartThin],
                page: {
                    searchKey: 'host.host_term_initiators_tab',
                    permission: ['host_group_R', 'initiator_R'],
                },
            },

            /**
             *  端口组
             */
            [DataTypeE.portGroup]: {
                type: DataTypeE.portGroup,
                url: '/service/block/port-group',
                license: [LicenseKey.SmartThin],
                operation: [
                    {
                        searchKey: 'host.portGroup_task_create_title',
                        action: 'searchCreate',
                        permission: ['port_group_R', 'port_group_W'],
                    },
                ],
                page: {
                    searchKey: 'common.portGroups_title',
                    permission: ['port_group_R'],
                    defaultAdvancedSearch: true,
                    advancedType: this.str.i18n.serviceTitle,
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.portgroup).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.portCountLab,
                                    value: data.portNum,
                                },
                                {
                                    label: this.str.i18n.mapStatusLab,
                                    value: data.isAddToMappingView === 'true' ? this.str.i18n.mapped : this.str.i18n.unMapped,
                                },
                            ];

                            return result;
                        }),
                    ),
            },

            /**
             *  VVol
             */
            ...(!FeatureConfig.IsSupportVVOL ? {} : {
                /**
                 *  PE LUN
                 */
                [DataTypeE.peLun]: {
                    type: DataTypeE.peLun,
                    url: '/service/block/vvol',
                    tab: this.str.i18n.peLunTab,
                    license: [LicenseKey.SmartThin],
                    operation: [
                        {
                            searchKey: 'storage.vvol_task_createPELUN_tittle',
                            action: 'searchCreate',
                            permission: ['lun_R', 'lun_W'],
                        },
                    ],
                    page: {
                        searchKey: 'storage.vvol_task_PELUN_tab',
                        permission: ['lun_R'],
                    },
                    hide: !FeatureConfig.IsSupportVVOL,
                    getCombinationObservable: (id: string): Observable<Tip[]> =>
                        this.configService.getData(id, DataURL.lun).pipe(
                            map(({ data }) => {
                                const protection = pick(JSON.parse(data.HASRSSOBJECT), [
                                    'SnapShot',
                                    'LunClone',
                                    'HyperCDP',
                                    'RemoteReplication',
                                    'HyperMetro',
                                    'drStar',
                                ]);
                                const result: Tip[] = [
                                    {
                                        label: this.str.i18n.healthStatusLab,
                                        status: parseInt(data.HEALTHSTATUS, 10),
                                        value: this.str.statusMap[parseInt(data.HEALTHSTATUS, 10)].label,
                                    },
                                    {
                                        label: this.str.i18n.mapStatusLab,
                                        value: data.mapped === 'true' ? this.str.i18n.mapped : this.str.i18n.unMapped,
                                    },
                                    {
                                        label: this.str.i18n.usageLab,
                                        value: this.utilService.adaptUnit(data.ALLOCCAPACITY, 'Sector').label,
                                    },
                                    {
                                        label: this.str.i18n.protectLab,
                                        icons: [
                                            ...(protection.SnapShot === 'TRUE' ? ['app-icon-snapshot'] : []),
                                            ...(protection.LunClone === 'TRUE' ? ['app-icon-clone'] : []),
                                            ...(protection.HyperCDP === 'TRUE' ? ['app-icon-hyperCDP'] : []),
                                            ...(protection.RemoteReplication === 'TRUE' ? ['app-icon-replication'] : []),
                                            ...(protection.HyperMetro === 'TRUE' ? ['app-icon-hypermetro'] : []),
                                            ...(protection.drStar === 'TRUE' ? ['app-icon-3dc'] : []),
                                        ],
                                    },
                                ];

                                return result;
                            }),
                        ),
                },
                /**
                 *  VVol LUN
                 */
                [DataTypeE.vvolLun]: {
                    type: DataTypeE.vvolLun,
                    url: '/service/block/vvol',
                    tab: this.str.i18n.vvolLunTab,
                    license: [LicenseKey.SmartThin],
                    page: {
                        searchKey: 'storage.vvol_task_vvolLUN_tab',
                        permission: ['lun_R'],
                    },
                    hide: !FeatureConfig.IsSupportVVOL,
                    getCombinationObservable: (id: string): Observable<Tip[]> =>
                        this.configService.getData(id, DataURL.lun).pipe(
                            map(({ data }) => {
                                const protection = pick(JSON.parse(data.HASRSSOBJECT), [
                                    'SnapShot',
                                    'LunClone',
                                    'HyperCDP',
                                    'RemoteReplication',
                                    'HyperMetro',
                                    'drStar',
                                ]);
                                const result: Tip[] = [
                                    {
                                        label: this.str.i18n.healthStatusLab,
                                        status: parseInt(data.HEALTHSTATUS, 10),
                                        value: this.str.statusMap[parseInt(data.HEALTHSTATUS, 10)].label,
                                    },
                                    {
                                        label: this.str.i18n.mapStatusLab,
                                        value: data.mapped === 'true' ? this.str.i18n.mapped : this.str.i18n.unMapped,
                                    },
                                    {
                                        label: this.str.i18n.usageLab,
                                        value: this.utilService.adaptUnit(data.ALLOCCAPACITY, 'Sector').label,
                                    },
                                    {
                                        label: this.str.i18n.protectLab,
                                        icons: [
                                            ...(protection.SnapShot === 'TRUE' ? ['app-icon-snapshot'] : []),
                                            ...(protection.LunClone === 'TRUE' ? ['app-icon-clone'] : []),
                                            ...(protection.HyperCDP === 'TRUE' ? ['app-icon-hyperCDP'] : []),
                                            ...(protection.RemoteReplication === 'TRUE' ? ['app-icon-replication'] : []),
                                            ...(protection.HyperMetro === 'TRUE' ? ['app-icon-hypermetro'] : []),
                                            ...(protection.drStar === 'TRUE' ? ['app-icon-3dc'] : []),
                                        ],
                                    },
                                ];

                                return result;
                            }),
                        ),
                },
                /**
                 * VVol保护组
                 */
                [DataTypeE.vvolPg]: {
                    type: DataTypeE.vvolPg,
                    url: '/service/block/vvol',
                    tab: this.str.i18n.vvolPgTab,
                    license: [LicenseKey.SmartThin],
                    page: {
                        searchKey: 'storage.vvol_task_vvolPG_tab',
                        permission: ['protect_group_R'],
                    },
                    hide: !FeatureConfig.IsSupportVVOL,
                },
            }),

            /**
             * 通道LUN
             */
            [DataTypeE.channelLun]: {
                type: DataTypeE.channelLun,
                url: '/service/block/channel-lun',
                tab: this.str.i18n.channelLunTab,
                license: [LicenseKey.SmartThin],
                hide: !FeatureConfig.IsSupportChannelLun,
                operation: [
                    {
                        searchKey: 'storage.channelLUN_task_create_title',
                        action: 'searchCreate',
                        permission: ['lun_R', 'lun_W'],
                    },
                ],
                page: {
                    searchKey: 'storage.channelLUN_task_title',
                    permission: ['lun_R'],
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.lun).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.healthStatusLab,
                                    status: parseInt(data.HEALTHSTATUS, 10),
                                    value: this.str.statusMap[parseInt(data.HEALTHSTATUS, 10)].label,
                                },
                                {
                                    label: this.str.i18n.mapStatusLab,
                                    value: data.mapped === 'true' ? this.str.i18n.mapped : this.str.i18n.unMapped,
                                },
                                {
                                    label: this.str.i18n.usageLab,
                                    value: this.utilService.adaptUnit(data.ALLOCCAPACITY, 'Sector').label,
                                },
                            ];

                            return result;
                        }),
                    ),
            },

            /**
             * 映射视图
             */
            [DataTypeE.mappingView]: {
                type: DataTypeE.mappingView,
                url: '/service/block/mapping-views',
                license: [LicenseKey.SmartThin],
                page: {
                    searchKey: 'storage.mappingView_task_title',
                    permission: ['mapping_view_R'],
                },
            },

            /**
             *  文件系统
             */
            [DataTypeE.fs]: {
                type: DataTypeE.fs,
                url: '/service/file/file-system',
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'service.fileSystem_term_create_title',
                        action: 'searchCreate',
                        permission: ['file_system_R', 'file_system_W'],
                        defaultSearch: true,
                    },
                ],
                page: {
                    searchKey: 'common.fileSystem_title',
                    permission: ['file_system_R'],
                    defaultAdvancedSearch: true,
                    advancedType: this.str.i18n.serviceTitle,
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.fs).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.healthStatusLab,
                                    status: parseInt(data.HEALTHSTATUS, 10),
                                    value: this.str.statusMap[parseInt(data.HEALTHSTATUS, 10)].label,
                                },
                                {
                                    label: this.str.i18n.usageLab,
                                    value: this.utilService.adaptUnit(data.allocatedPoolQuota, 'Sector').label,
                                },
                                {
                                    label: this.str.i18n.runStatusLab,
                                    value: this.keyValuePipe.transform(data.RUNNINGSTATUS, runningStatusE),
                                },
                            ];

                            return result;
                        }),
                    ),
            },

            /**
             *  dtree
             *  因为这些对象是透传的，CFG 数据库没有保存，所以不支持搜索
             */
            [DataTypeE.dtree]: {
                type: DataTypeE.dtree,
                url: '/service/file/dtree',
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'service.dtree_term_create_title',
                        action: 'create',
                        permission: ['dtree_R', 'dtree_W'],
                    },
                ],
                page: {
                    searchKey: 'common.dtree_lab',
                    permission: ['dtree_R'],
                    defaultAdvancedSearch: true,
                    advancedType: this.str.i18n.serviceTitle,
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.dtree).pipe(
                        map(({ data }) => {
                            const parse = (value): { [key: string]: boolean } => {
                                // eslint-disable-next-line @typescript-eslint/no-shadow
                                const result: any = {};
                                value.forEach((item) => {
                                    result[item] = true;
                                });

                                return result;
                            };

                            const type = parse(data.shareTypes);
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.shareLab,
                                    icons: [...(type.NFS ? ['app-icon-nfs'] : []), ...(type.CIFS ? ['app-icon-cifs'] : [])],
                                },
                                {
                                    label: this.str.i18n.quotaLab,
                                    value: data.quotaCfgCount,
                                },
                            ];

                            return result;
                        }),
                    ),
            },

            /**
             *  NFS共享
             *  因为这些对象是透传的，CFG 数据库没有保存，所以不支持搜索
             */
            [DataTypeE.nfsShare]: {
                type: DataTypeE.nfsShare,
                url: '/service/file/share',
                tab: this.str.i18n.nfsShareTab,
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'service.share_term_createNFS_title',
                        action: 'create',
                        permission: ['share_R', 'share_W'],
                        defaultSearch: true,
                    },
                ],
                page: {
                    searchKey: 'common.nfsShare_lab',
                    permission: ['share_R'],
                    defaultSearch: true,
                    defaultAdvancedSearch: true,
                    advancedType: this.str.i18n.serviceTitle,
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.nfsshare).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.sharePathLab,
                                    value: data.SHAREPATH,
                                },
                            ];

                            return result;
                        }),
                    ),
            },
            /**
             *  CIFS共享
             *  因为这些对象是透传的，CFG 数据库没有保存，所以不支持搜索
             */
            [DataTypeE.cifsShare]: {
                type: DataTypeE.cifsShare,
                url: '/service/file/share',
                tab: this.str.i18n.cifsShareTab,
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'service.share_term_createCIFS_title',
                        action: 'create',
                        permission: ['share_R', 'share_W'],
                    },
                ],
                page: {
                    searchKey: 'common.cifsShare_lab',
                    permission: ['share_R'],
                    defaultAdvancedSearch: true,
                    advancedType: this.str.i18n.serviceTitle,
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.cifsshare).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.sharePathLab,
                                    value: data.SHAREPATH,
                                },
                            ];

                            return result;
                        }),
                    ),
                hide: ProductConfig.isHideCifs,
            },
            /**
             *  dataTurbo共享
             *  因为这些对象是透传的，CFG 数据库没有保存，所以不支持搜索
             */
            ...(FeatureConfig.IsSupportDataTurboWhenNASIsSupported || FeatureConfig.IsSupportDpcWhenNASIsSupported) && !SWITCH.IS_VSTORE_SYSTEM ? {
                [DataTypeE.dataTurboShare]: {
                    type: DataTypeE.dataTurboShare,
                    url: '/service/file/share',
                    tab: this.str.i18n.dataTurboShareTab,
                    isNASFeature: true,
                    operation: [
                        {
                            searchKey: 'service.share_term_createDataTurbo_title',
                            action: 'create',
                            permission: ['share_W'],
                        },
                    ],
                    page: {
                        searchKey: 'common.DataTurboShare_lab',
                        permission: ['share_R'],
                        defaultAdvancedSearch: true,
                        advancedType: this.str.i18n.serviceTitle,
                    },
                    getCombinationObservable: (id: string): Observable<Tip[]> =>
                        this.configService.getData(id, DataURL.dataTurboshare).pipe(
                            map(({ data }) => {
                                const result: Tip[] = [
                                    {
                                        label: this.str.i18n.sharePathLab,
                                        value: data.SHAREPATH,
                                    },
                                ];

                                return result;
                            }),
                        ),
                },
            } : {},
            /**
             *  UNIX用户
             */
            [DataTypeE.unixUser]: {
                type: DataTypeE.unixUser,
                url: '/service/file/access-user',
                tab: this.str.i18n.unixUserTab,
                isNASFeature: true,
                page: {
                    searchKey: 'service.accessUser_UNIXUsers_lab',
                    permission: ['share_R'],
                },
            },
            /**
             *  Windows用户
             */
            [DataTypeE.windowsUser]: {
                type: DataTypeE.windowsUser,
                url: '/service/file/access-user',
                tab: this.str.i18n.windowsUserTab,
                isNASFeature: true,
                page: {
                    searchKey: 'service.accessUser_WindowsUsers_lab',
                    permission: ['share_R'],
                },
                hide: ProductConfig.isHideWindowsUserFeature,
            },
            /**
             *  用户映射
             */
            [DataTypeE.userMapping]: {
                type: DataTypeE.userMapping,
                url: '/service/file/access-user',
                tab: this.str.i18n.userMappingTab,
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'service.userMapping_task_create_title',
                        action: 'create',
                        permission: ['share_R', 'share_W'],
                    },
                ],
                page: {
                    searchKey: 'service.userMapping_task_title',
                    permission: ['share_R'],
                },
                hide: ProductConfig.isHideWindowsUserFeature,
            },

            /**
             *  配额报表
             */
            [DataTypeE.quotaReports]: {
                type: DataTypeE.quotaReports,
                url: '/service/file/quota',
                tab: this.str.i18n.quotaReportsTab,
                isNASFeature: true,
                page: {
                    searchKey: 'service.quota_term_quotaReports_tittle',
                    permission: ['quota_R'],
                },
            },
            /**
             *  自定义配额
             *  因为这些对象是透传的，CFG 数据库没有保存，所以不支持搜索
             */
            [DataTypeE.quota]: {
                type: DataTypeE.quota,
                url: '/service/file/quota',
                tab: this.str.i18n.quotaTab,
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'service.quota_term_quotaMana_create_title',
                        action: 'create',
                        permission: ['quota_R', 'quota_W'],
                    },
                ],
                page: {
                    searchKey: 'service.quota_term_userQuotas_tittle',
                    permission: ['quota_R'],
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.quota).pipe(
                        map(({ data }) => {
                            // eslint-disable-next-line @typescript-eslint/no-shadow
                            const spaceParse = (quotaData: Quota): string => {
                                const hard = this.toolService.isInvalidNum(quotaData.SPACEHARDQUOTA, true) ?
                                    UNPLACEHOLDER :
                                    this.utilService.adaptUnit(quotaData.SPACEHARDQUOTA, CAPACITY_UNIT.KB).label;

                                const soft = this.toolService.isInvalidNum(quotaData.SPACESOFTQUOTA, true) ?
                                    UNPLACEHOLDER :
                                    this.utilService.adaptUnit(quotaData.SPACESOFTQUOTA, CAPACITY_UNIT.KB).label;

                                return `${soft}/${hard}`;
                            };

                            // eslint-disable-next-line @typescript-eslint/no-shadow
                            const fsParse = (quotaData: Quota): string => {
                                const hard = this.toolService.isInvalidNum(quotaData.FILEHARDQUOTA, true) ? UNPLACEHOLDER : quotaData.FILEHARDQUOTA;

                                const soft = this.toolService.isInvalidNum(quotaData.FILESOFTQUOTA, true) ? UNPLACEHOLDER : quotaData.FILESOFTQUOTA;

                                return `${soft}/${hard}`;
                            };

                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.spaceHardquotaLab,
                                    value: spaceParse(data),
                                },
                                {
                                    label: this.str.i18n.fileHardquotaLab,
                                    value: fsParse(data),
                                },
                            ];

                            return result;
                        }),
                    ),
            },
            /**
             * dataTurbo客户端
             */
            [DataTypeE.dataTurboClient]: {
                type: DataTypeE.dataTurboClient,
                url: '/service/file/data-turbo-client',
                isNASFeature: true,
                page: {
                    searchKey: 'service.DataTurboClient_title',
                    permission: ['data_turbo_R'],
                },
                hide: !FeatureConfig.IsSupportDataTurboWhenNASIsSupported,
            },

            /**
             *  逻辑端口
             */
            [DataTypeE.logicPort]: {
                type: DataTypeE.logicPort,
                url: '/service/network/lif',
                operation: [
                    {
                        searchKey: 'system.systemSetting_network_task_createLIF_button',
                        action: 'onCreate',
                        permission: ['port_R', 'port_W'],
                    },
                ],
                page: {
                    searchKey: 'common.LogicalPorts',
                    permission: ['port_R'],
                },
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.lif).pipe(
                        map(({ data }) => {
                            const result: Tip[] = [
                                {
                                    label: this.str.i18n.activeStatusLab,
                                    value: this.hardStrService.lifActiveOpt[data.OPERATIONALSTATUS],
                                },
                                {
                                    label: this.str.i18n.runStatusLab,
                                    value: this.lifColumnDefsService.generateOpt(runningStatusE)[data.RUNNINGSTATUS],
                                },
                                {
                                    label: this.str.i18n.protocolLab,
                                    value:
                                        data.SUPPORTPROTOCOL === '0' || !data.SUPPORTPROTOCOL ?
                                            '' :
                                            this.hardStrService.lifProtocolOpt[data.SUPPORTPROTOCOL],
                                },
                            ];

                            return result;
                        }),
                    ),
            },

            /**
             *  以太网端口
             */
            [DataTypeE.ethNetwork]: {
                type: DataTypeE.ethNetwork,
                url: '/service/network/eth',
                tab: this.str.i18n.ethNetworkTab,
                page: {
                    searchKey: 'system.network_term_etherPorts_tab',
                    permission: ['port_R'],
                },
            },

            /**
             *  绑定端口
             */
            [DataTypeE.bindPorts]: {
                type: DataTypeE.bindPorts,
                url: '/service/network/eth',
                tab: this.str.i18n.bindPortsTab,
                operation: [
                    {
                        searchKey: 'system.systemSetting_network_task_createBondPort_title_wintitle',
                        action: 'onCreate',
                        permission: ['port_R', 'port_W'],
                        noBondFeature: !FeatureConfig.IsSupportCreateBondPort,
                    },
                ],
                page: {
                    searchKey: 'system.network_term_bondPorts_tab',
                    permission: ['port_R'],
                },
            },
            /**
             *  以太网络VLAN
             */
            [DataTypeE.ethVlan]: {
                type: DataTypeE.ethVlan,
                url: '/service/network/eth',
                tab: this.str.i18n.ethVlanTab,
                isNotSupportFeatureInOceanMicro: SWITCH.IS_DIRECT_MODE,
                operation: [
                    {
                        searchKey: 'home.search_term_createEthVlan_lab',
                        action: 'onCreate',
                        permission: ['port_R', 'port_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_ethVlan_lab',
                    permission: ['port_R'],
                },
            },
            /**
             *  漂移组
             */
            [DataTypeE.failoverGroups]: {
                type: DataTypeE.failoverGroups,
                url: '/service/network/eth',
                tab: this.str.i18n.failoverGroupsTab,
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'home.search_term_createEthernetFailoverGroup_lab',
                        action: 'create',
                        permission: ['port_R', 'port_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_EthernetFailoverGroup_lab',
                    permission: ['port_R'],
                },
            },
            /**
             *  虚拟交换机
             */
            [DataTypeE.vSwitch]: {
                type: DataTypeE.vSwitch,
                url: '/service/network/eth',
                tab: this.str.i18n.vSwitchTab,
                isNASFeature: true,
                page: {
                    searchKey: 'compute.service_network_vSwitch_tab',
                    permission: ['port_R'],
                },
                hide: !FeatureConfig.IsShowVSwitch,
            },

            // 二级存储,计算型存储,入门级存储屏蔽RoCE端口及RoCE VLAN
            ...!FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif ? {} : {
                /**
                 *  RoCE端口
                 */
                [DataTypeE.rocePorts]: {
                    type: DataTypeE.rocePorts,
                    url: '/service/network/roce',
                    tab: this.str.i18n.rocePortsTab,
                    page: {
                        searchKey: 'system.network_term_rocePorts_tab',
                        permission: ['port_R'],
                        noRoceFeature: !FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif,
                    },
                },
                /**
                 *  RoCE VLAN
                 */
                [DataTypeE.roceVlan]: {
                    type: DataTypeE.roceVlan,
                    url: '/service/network/roce',
                    tab: this.str.i18n.roceVlanTab,
                    operation: [
                        {
                            searchKey: 'home.search_term_createRoceVlan_lab',
                            action: 'onCreate',
                            permission: ['port_R', 'port_W'],
                            noRoceFeature: !FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif,
                        },
                    ],
                    page: {
                        searchKey: 'home.search_term_roceVlan_lab',
                        permission: ['port_R'],
                        noRoceFeature: !FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif,
                    },
                },
                /**
                 *  RoCE 漂移组
                 */
                [DataTypeE.roceVlan]: {
                    type: DataTypeE.roceFailoverGroups,
                    url: '/service/network/roce',
                    tab: this.str.i18n.roceFailoverGroupsTab,
                    operation: [
                        {
                            searchKey: 'home.search_term_createRoceFailoverGroup_lab',
                            action: 'create',
                            permission: ['port_R', 'port_W'],
                            // 除RDMA的功能开关外，还需要跟上面ROCE 端口/VLAN的保持一致
                            noRoceFeature: !SWITCH.SUPPORT_NFS_OVER_RDMA ||
                                !FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif,
                        },
                    ],
                    page: {
                        searchKey: 'home.search_term_RoceFailoverGroup_lab',
                        permission: ['port_R'],
                        // 除RDMA的功能开关外，还需要跟上面ROCE 端口/VLAN的保持一致
                        noRoceFeature: !SWITCH.SUPPORT_NFS_OVER_RDMA ||
                            !FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif,
                    },
                },
            },

            /**
             *  FC网络
             */
            [DataTypeE.fcNet]: {
                type: DataTypeE.fcNet,
                url: '/service/network/fc',
                page: {
                    searchKey: 'system.network_fc_tiitle',
                    permission: ['port_R'],
                    noFcFeature: !FeatureConfig.IsSupportFC,
                },
                hide: SWITCH.IS_DIRECT_MODE,
            },

            /**
             *  netSpace
             */
            [DataTypeE.netSpace]: {
                type: DataTypeE.netSpace,
                url: '/service/network/net-space',
                getCombinationObservable: (id: string): Observable<Tip[]> =>
                    this.configService.getData(id, DataURL.netSpace).pipe(
                        map(({ data }: { data: NetSpace }) => {
                            const result: Tip[] = [
                                {
                                    label: this.netSpaceStrService.i18n.netSpaceTypeWithColon,
                                    value: this.netSpaceStrService.netSpaceTypeStrOpt(data.netspaceType),
                                },
                            ];

                            return result;
                        }),
                    ),
                operation: [
                    {
                        searchKey: 'system.network_netSpace_create_title',
                        action: 'onCreate',
                        permission: ['netspace_W'],
                    },
                ],
                page: {
                    searchKey: 'system.network_netSpaces_title',
                    permission: ['netspace_R'],
                },
                hide: !SWITCH.SUPPORT_NET_SPACE,
            },

            /**
             *  交换机端口
             */
            [DataTypeE.SWITCH_PORT]: {
                type: DataTypeE.SWITCH_PORT,
                url: '/service/network/switch-port',
                page: {
                    searchKey: 'system.hardware_term_SOSwitchPorts_tab',
                    permission: ['port_R'],
                },
                hide: !SWITCH.SUPPORT_SHOW_SWITCH,
            },

            /**
             *  smartCache
             */
            [DataTypeE.smartCache]: {
                type: DataTypeE.smartCache,
                url: '/service/tuning/smart-cache',
                page: {
                    searchKey: 'storage.smartCache_term_title',
                    permission: ['smart_cache_R'],
                },
                hide: !FeatureConfig.IsSupportSmartCache,
            },

            /**
             * smartQos
             */
            ...(SWITCH.IS_DIRECT_MODE ? {} : {
                [DataTypeE.smartQos]: {
                    type: DataTypeE.smartQos,
                    url: '/service/tuning/smart-qos',
                    license: FeatureConfig.IsSupportSmartQosNoNeedLicense ? [] : [LicenseKey.SmartQoS],
                    operation: [
                        {
                            searchKey: 'storage.smartQoS_task_createQoSPolicy_title',
                            action: 'searchCreate',
                            permission: ['smart_qos_R', 'smart_qos_W'],
                        },
                    ],
                    page: {
                        searchKey: 'monitor.charts_term_objectType_SmartQoS',
                        permission: ['smart_qos_R'],
                    },
                },
            }),
            [DataTypeE.smartMigration]: {
                type: DataTypeE.smartMigration,
                url: '/service/tuning/smart-migration',
                license: [LicenseKey.SmartMigration, LicenseKey.SmartThin],
                operation: [
                    {
                        searchKey: 'storage.smartMigration_task_create_createSmartMigration_title',
                        action: 'searchCreate',
                        permission: ['smart_migration_R', 'smart_migration_W'],
                    },
                ],
                page: {
                    searchKey: 'storage.smartMigration_menu',
                    permission: ['smart_migration_R'],
                },
                hide: !FeatureConfig.IsSupportSmartMigration,
            },
            /**
             *  阵列内SmartMove
             */
            [DataTypeE.intraSmartMove]: {
                type: DataTypeE.intraSmartMove,
                url: '/service/tuning/fs-smart-move',
                tab: this.str.i18n.intraSmartMoveTab,
                license: [LicenseKey.SmartMove],
                operation: [
                    {
                        searchKey: 'storage.smartMove_task_create_createSmartMove_title',
                        action: 'searchCreate',
                        permission: ['smart_move_R', 'smart_move_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_withinArraySmartSmartMove_lab',
                    permission: ['smart_move_R'],
                },
                hide: !SWITCH.SUPPORT_SMART_MOVE,
            },
            /**
             *  阵列间SmartMove
             */
            [DataTypeE.extraSmartMove]: {
                type: DataTypeE.extraSmartMove,
                url: '/service/tuning/fs-smart-move',
                tab: this.str.i18n.extraSmartMoveTab,
                license: [LicenseKey.SmartMove],
                page: {
                    searchKey: 'home.search_term_betweenArraySmartSmartMove_lab',
                    permission: [['inter_smart_move_domain_R', 'inter_smart_move_vstore_R', 'inter_smart_move_fs_R']],
                },
                hide: !SWITCH.SUPPORT_SMART_MOVE,
            },
            /**
             *  阵列间SmartMove - 迁移域
             */
            [DataTypeE.smartMoveDomain]: {
                type: DataTypeE.smartMoveDomain,
                url: '/service/tuning/fs-smart-move',
                tab: this.str.i18n.extraSmartMoveTab,
                subTab: this.str.i18n.smartMoveDomainTab,
                license: [LicenseKey.SmartMove],
                operation: [
                    {
                        searchKey: 'storage.smartMove_task_createDomain_title',
                        action: 'createMoveDomain',
                        permission: ['remote_device_R', 'inter_smart_move_domain_W'],
                    },
                ],
                page: {
                    searchKey: 'storage.smartMove_task_domain_tab',
                    permission: ['inter_smart_move_domain_R'],
                },
                hide: !SWITCH.SUPPORT_SMART_MOVE,
            },
            /**
             *  阵列间SmartMove - 迁移租户pair
             */
            [DataTypeE.smartMoveVstorePair]: {
                type: DataTypeE.smartMoveVstorePair,
                url: '/service/tuning/fs-smart-move',
                tab: this.str.i18n.extraSmartMoveTab,
                subTab: this.str.i18n.smartMoveVstorePairTab,
                license: [LicenseKey.SmartMove],
                operation: [
                    {
                        searchKey: 'storage.smartMove_task_vstorePair_create_title',
                        action: 'searchCreate',
                        permission: [
                            'inter_smart_move_vstore_W',
                            'task_W',
                            '@vstore_R',
                            'inter_smart_move_domain_R',
                        ],
                    },
                ],
                page: {
                    searchKey: 'storage.smartMove_task_vstorePair_tab',
                    permission: ['inter_smart_move_vstore_R'],
                },
                hide: !SWITCH.SUPPORT_SMART_MOVE,
            },
            /**
             *  阵列间SmartMove - SmartMove文件系统 pair
             */
            [DataTypeE.smartMoveFsPair]: {
                type: DataTypeE.smartMoveFsPair,
                url: '/service/tuning/fs-smart-move',
                tab: this.str.i18n.extraSmartMoveTab,
                subTab: this.str.i18n.smartMoveFsPairTab,
                license: [LicenseKey.SmartMove],
                operation: [
                    {
                        searchKey: 'storage.smartMove_task_fsPair_create_title',
                        action: 'searchCreate',
                        permission: [
                            'file_system_R',
                            'storage_pool_R',
                            'inter_smart_move_vstore_R',
                            'inter_smart_move_domain_R',
                            'task_W',
                            'inter_smart_move_fs_W',
                        ],
                    },
                ],
                page: {
                    searchKey: 'storage.smartMove_task_fsPair_tab',
                    permission: ['inter_smart_move_fs_R'],
                },
                hide: !SWITCH.SUPPORT_SMART_MOVE,
            },

            /**
             * cloudTier
             */
            [DataTypeE.cloudTier]: {
                type: DataTypeE.cloudTier,
                url: '/service/tuning/smart-mobility',
                tab: this.str.i18n.cloudTierTab,
                license: [[LicenseKey.NAS], [LicenseKey.SmartMobilityNAS, LicenseKey.SmartMobilityObject]],
                operation: [
                    {
                        searchKey: 'home.search_term_createCloudTier_lab',
                        action: 'searchCreate',
                        permission: ['smart_mobility_W', 'file_system_R'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_CloudTier_lab',
                    permission: ['smart_mobility_R', 'file_system_R'],
                },
                hide: !ShardSwitch.CLOUD_TIER,
            },
            /**
             * cloudTier策略模板
             */
            [DataTypeE.tierPolicy]: {
                type: DataTypeE.tierPolicy,
                url: '/service/tuning/smart-mobility',
                tab: this.str.i18n.policyTab,
                license: [[LicenseKey.NAS], [LicenseKey.SmartMobilityNAS, LicenseKey.SmartMobilityObject]],
                operation: [
                    {
                        searchKey: 'home.search_term_createCloudTierPolicyTemplate_lab',
                        action: 'create',
                        permission: ['smart_mobility_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_CloudTierPolicyTemplate_lab',
                    permission: ['smart_mobility_R'],
                },
                hide: !ShardSwitch.CLOUD_TIER_TEMPLATE,
            },
            /**
             * cloudTier远端设备
             */
            [DataTypeE.tierDev]: {
                type: DataTypeE.tierDev,
                url: '/service/tuning/smart-mobility',
                tab: this.str.i18n.tierDevTab,
                license: [[LicenseKey.NAS], [LicenseKey.SmartMobilityNAS, LicenseKey.SmartMobilityObject]],
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'home.search_term_addCloudTierRemoteDevice_lab',
                        action: 'createTierDevice',
                        permission: ['smart_mobility_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_CloudTierRemoteDevice_lab',
                    permission: ['smart_mobility_R'],
                },
                hide: !ShardSwitch.CLOUD_TIER,
            },
            /**
             * cloudTier本端地址池
             */
            [DataTypeE.tierLocalIpgroup]: {
                type: DataTypeE.tierLocalIpgroup,
                url: '/service/tuning/smart-mobility',
                tab: this.str.i18n.tierLocalIpgroupTab,
                license: [[LicenseKey.NAS], [LicenseKey.SmartMobilityNAS, LicenseKey.SmartMobilityObject]],
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'home.search_term_createCloudTierLocalIPGroup_lab',
                        action: 'create',
                        permission: ['smart_mobility_W', 'port_R'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_CloudTierLocalIPGroup_lab',
                    permission: ['smart_mobility_R'],
                },
                hide: !ShardSwitch.CLOUD_TIER,
            },
            /**
             * cloudTier远端地址池
             */
            [DataTypeE.tierRemoteIpgroup]: {
                type: DataTypeE.tierRemoteIpgroup,
                url: '/service/tuning/smart-mobility',
                tab: this.str.i18n.tierRemoteIpgroupTab,
                license: [[LicenseKey.NAS], [LicenseKey.SmartMobilityNAS, LicenseKey.SmartMobilityObject]],
                isNASFeature: true,
                operation: [
                    {
                        searchKey: 'home.search_term_createCloudTierRemoteIPGroup_lab',
                        action: 'create',
                        permission: ['smart_mobility_W'],
                    },
                ],
                page: {
                    searchKey: 'home.search_term_CloudTierRemoteIPGroup_lab',
                    permission: ['smart_mobility_R'],
                },
                hide: !ShardSwitch.CLOUD_TIER,
            },

            /**
             * pod
             */
            [DataTypeE.pod]: {
                type: DataTypeE.pod,
                url: '/service/container-storage/pod',
                page: {
                    searchKey: 'storage.containerStorage_task_pod_title',
                    permission: ['container_storage_R'],
                },
                hide: !FeatureConfig.IsSupportContainerStorage,
            },

            /**
             * pv
             */
            [DataTypeE.pv]: {
                type: DataTypeE.pv,
                url: '/service/container-storage/pv',
                page: {
                    searchKey: 'storage.containerStorage_task_pv_title',
                    permission: ['container_storage_R'],
                },
                hide: !FeatureConfig.IsSupportContainerStorage,
            },

            /**
             * NFS客户端
             */
            [DataTypeE.nfsClient]: {
                type: DataTypeE.nfsClient,
                url: '/service/file/nfs-client',
                isNASFeature: true,
                page: {
                    searchKey: 'service.NFSClient_term_title',
                    permission: ['nfs_service_R'],
                },
                hide: !FeatureConfig.IsSupportNFSClient,
            },
        };

        return this.servicesDataMap;
    }
}
